import React, { useContext } from "react"
import { Box, Text } from "@chakra-ui/react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $getNodeByKey } from "lexical"
import Menu from "components/dialogs/Menu"
import { MergeFieldMenuGroup, MergeFieldMenuItem } from "components/MergeFieldMenu"
import { PillShell } from "components/TemplateEditor/VariablePill"
import { NAME_FIELD_TAGS } from "utilities/enums"
import { MergeTagEditorContext } from "./MergeTagEditorContext"
import GripIcon from "./GripIcon"
import { handleMergeTagDragStart } from "./mergeTagDrag"
import MergeTagPillDisplay from "./MergeTagPillDisplay"
import { $createMergeTagNode, $isMergeTagNode, $removeMergeTagByKey } from "./MergeTagNode"
import { registerMergeTagComponent } from "./mergeTagComponentRegistry"
import { SUBJECT_LINE_MERGE_TAGS } from "./subjectLineTags"

const NAME_FIELD_OPTIONS = [
  { value: "FIRST_NAME", label: "First Name" },
  { value: "FULL_NAME", label: "Full Name" },
  { value: "LAST_NAME", label: "Last Name" },
]

type Props = {
  tagId: string
  tagLabel: string
  nodeKey: string
}

const MergeTagDraggableWrapper = ({
  nodeKey, tagId, tagLabel, children,
}: {
  nodeKey: string
  tagId: string
  tagLabel: string
  children: React.ReactNode
}) => (
  <Box
    as="span"
    display="inline"
    draggable
    cursor="grab"
    _active={{ cursor: "grabbing" }}
    onDragStart={(e: React.DragEvent<HTMLElement>) => {
      handleMergeTagDragStart(e, { nodeKey, tagId, tagLabel })
    }}
  >
    {children}
  </Box>
)

const MergeTagPillRoot = ({
  nodeKey, tagId, tagLabel, children,
}: {
  nodeKey: string
  tagId: string
  tagLabel: string
  children: React.ReactNode
}) => (
  <span
    contentEditable={false}
    suppressContentEditableWarning
    data-merge-tag-pill=""
    draggable
    style={{ display: "inline", cursor: "grab" }}
    onDragStart={(e: React.DragEvent<HTMLElement>) => {
      handleMergeTagDragStart(e, { nodeKey, tagId, tagLabel })
    }}
  >
    {children}
  </span>
)

const MergeTagComponent = ({ tagId, tagLabel, nodeKey }: Props) => {
  const [editor] = useLexicalComposerContext()
  const { groupedForms, onSave, subjectLineMode } = useContext(MergeTagEditorContext)

  const isFormLink = tagId.startsWith("FORM_")
  const isNameField = NAME_FIELD_TAGS.includes(tagId)

  const removeTag = () => {
    editor.update(() => {
      $removeMergeTagByKey(nodeKey)
    })
    onSave?.()
  }

  const replaceTag = (newTagId: string, label: string) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey)
      if ($isMergeTagNode(node)) {
        node.replace($createMergeTagNode(newTagId, label))
      }
    })
    onSave?.()
  }

  if (subjectLineMode) {
    const menuItems = [
      <MergeFieldMenuGroup key="guest-fields" title="Guest Fields">
        {SUBJECT_LINE_MERGE_TAGS.map(({ id, label }) => (
          <MergeFieldMenuItem
            key={id}
            onClick={() => replaceTag(id, label)}
            selected={id === tagId}
          >
            {label}
          </MergeFieldMenuItem>
        ))}
      </MergeFieldMenuGroup>,
    ]

    return (
      <MergeTagPillRoot nodeKey={nodeKey} tagId={tagId} tagLabel={tagLabel}>
        <MergeTagPillDisplay
          variant="selectable"
          tagLabel={tagLabel}
          menuItems={menuItems}
          onRemove={removeTag}
        />
      </MergeTagPillRoot>
    )
  }

  if (isNameField) {
    const nameMenuItems = NAME_FIELD_OPTIONS.map((option) => (
      <MergeFieldMenuItem
        key={option.value}
        onClick={() => replaceTag(option.value, option.label)}
        selected={option.value === tagId}
      >
        {option.label}
      </MergeFieldMenuItem>
    ))

    return (
      <MergeTagDraggableWrapper nodeKey={nodeKey} tagId={tagId} tagLabel={tagLabel}>
        <Menu
          placement="bottom"
          noPadding
          button={(
            <PillShell variant="selectable" size="md" cursor="grab">
              <GripIcon />
              <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{tagLabel}</Text>
            </PillShell>
          )}
          menuItems={nameMenuItems}
          maxH="50vh"
        />
      </MergeTagDraggableWrapper>
    )
  }

  if (!isFormLink) {
    return (
      <MergeTagDraggableWrapper nodeKey={nodeKey} tagId={tagId} tagLabel={tagLabel}>
        <PillShell variant="locked" size="md" cursor="grab">
          <GripIcon />
          <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{tagLabel}</Text>
        </PillShell>
      </MergeTagDraggableWrapper>
    )
  }

  const currentFormId = tagId.replace("FORM_", "")

  const menuItems = groupedForms.flatMap((group) => [
    <MergeFieldMenuGroup
      key={group.label}
      title={group.label}
    >
      {group.options.map((option) => (
        <MergeFieldMenuItem
          key={option.value}
          onClick={() => replaceTag(`FORM_${option.value}`, option.label)}
          selected={option.value === currentFormId}
          isDisabled={option.disabled}
        >
          {option.label}
        </MergeFieldMenuItem>
      ))}
    </MergeFieldMenuGroup>,
  ])

  return (
    <MergeTagDraggableWrapper nodeKey={nodeKey} tagId={tagId} tagLabel={tagLabel}>
      <Menu
        placement="bottom"
        noPadding
        button={(
          <PillShell variant="selectable" size="md" cursor="grab">
            <GripIcon />
            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="250px">{tagLabel}</Text>
          </PillShell>
        )}
        menuItems={menuItems}
        maxH="50vh"
      />
    </MergeTagDraggableWrapper>
  )
}

registerMergeTagComponent(MergeTagComponent)

export default MergeTagComponent
